<template>
  <div class="page branch-rank">
    <header class="branch-rank__head">
      <div class="branch-rank__title">
        <h2>分会核心指标排名</h2>
        <p>点击柱条下钻到该分会独立数据大屏 · 结构与全域总览一致</p>
      </div>
      <div class="metric-tabs" role="tablist">
        <button
          v-for="m in metrics"
          :key="m.key"
          type="button"
          role="tab"
          class="metric-tabs__btn"
          :class="{ active: metric === m.key }"
          :aria-selected="metric === m.key"
          @click="switchMetric(m.key)"
        >
          {{ m.label }}
        </button>
      </div>
    </header>

    <div class="branch-rank__body">
      <p v-if="!sorted.length" class="empty">暂无分会数据，请在后台「分会管理」维护</p>
      <div v-else class="bars" :style="{ '--bar-max': String(maxValue || 1) }">
        <button
          v-for="(row, i) in sorted"
          :key="row.id"
          type="button"
          class="bar-row"
          :class="'medal-' + Math.min(i + 1, 4)"
          :style="{ '--i': i }"
          @click="drill(row)"
        >
          <span
            class="bar-row__rank"
            :class="{ 'is-medal': i < 3 }"
          >{{ i + 1 }}</span>
          <div class="bar-row__track">
            <div
              class="bar-row__fill"
              :style="{ width: barWidth(row) }"
            >
              <span class="bar-row__shine" aria-hidden="true" />
            </div>
            <div class="bar-row__tip">
              <strong>{{ formatValue(row) }}</strong>
              <em :class="deltaTone(row)">{{ deltaText(row) }}</em>
            </div>
          </div>
          <div class="bar-row__label">
            <strong>{{ row.name }}</strong>
            <span>{{ formatValue(row) }}</span>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { isSameJson } from "@/utils/stableData";

export default {
  name: "DashBranch",
  props: {
    rem: { type: Number, default: 1 },
    commonFont: { type: Number, default: 14 }
  },
  data() {
    return {
      ranking: [],
      metric: "activityCount",
      metrics: [
        { key: "activityCount", label: "年度活动数", unit: "场", deltaUnit: "场" },
        { key: "volunteerHours", label: "公益总时长", unit: "小时", deltaUnit: "小时" },
        { key: "memberCount", label: "会员总数", unit: "人", deltaUnit: "人" },
        { key: "sandanRate", label: "三单办结率", unit: "%", deltaUnit: "个百分点" }
      ],
      refreshTimer: null,
      _pageLive: false
    };
  },
  computed: {
    metricMeta() {
      return this.metrics.find(m => m.key === this.metric) || this.metrics[0];
    },
    sorted() {
      const key = this.metric;
      return (this.ranking || [])
        .slice()
        .sort((a, b) => {
          const dv = (Number(b[key]) || 0) - (Number(a[key]) || 0);
          if (dv !== 0) return dv;
          return String(a.name || "").localeCompare(String(b.name || ""), "zh");
        });
    },
    maxValue() {
      if (!this.sorted.length) return 1;
      return Math.max(...this.sorted.map(r => Number(r[this.metric]) || 0), 1);
    }
  },
  created() {
    this._pageLive = true;
    this.load();
    this.refreshTimer = setInterval(() => {
      if (!this._pageLive) return;
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      this.load();
    }, 10000);
  },
  beforeDestroy() {
    this._pageLive = false;
    clearInterval(this.refreshTimer);
  },
  methods: {
    async load() {
      try {
        const res = await this.$api("screen", "branches", {});
        if (res && res.code === 0) {
          const next = res.ranking || [];
          if (isSameJson(this.ranking, next)) return;
          this.ranking = next;
        }
      } catch (e) {
        /* keep previous */
      }
    },
    switchMetric(key) {
      if (this.metric === key) return;
      this.metric = key;
    },
    valueOf(row) {
      return Number(row && row[this.metric]) || 0;
    },
    deltaOf(row) {
      const map = {
        activityCount: "activityDelta",
        volunteerHours: "volunteerHoursDelta",
        memberCount: "memberDelta",
        sandanRate: "sandanRateDelta"
      };
      return Number(row && row[map[this.metric]]) || 0;
    },
    barWidth(row) {
      const v = this.valueOf(row);
      const pct = Math.max(4, Math.round((100 * v) / this.maxValue));
      return `${Math.min(100, pct)}%`;
    },
    formatValue(row) {
      const v = this.valueOf(row);
      const unit = this.metricMeta.unit;
      if (this.metric === "sandanRate") return `${v}${unit}`;
      return `${v} ${unit}`;
    },
    deltaText(row) {
      const d = this.deltaOf(row);
      const unit = this.metricMeta.deltaUnit;
      if (d === 0) return "较上月 持平";
      const sign = d > 0 ? "+" : "";
      return `较上月 ${sign}${d} ${unit}`;
    },
    deltaTone(row) {
      const d = this.deltaOf(row);
      if (d > 0) return "up";
      if (d < 0) return "down";
      return "flat";
    },
    drill(row) {
      if (!row || row.id == null) return;
      this.$router.push({ path: "/overview", query: { branchId: row.id } });
    }
  }
};
</script>

<style lang="less" scoped>
.page.branch-rank {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14 / @rem2;
  min-height: 0;
}
.branch-rank__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16 / @rem2;
  flex-shrink: 0;
}
.branch-rank__title {
  h2 {
    margin: 0;
    color: @mainColor;
    font-size: 22 / @rem2;
    letter-spacing: 0.04em;
  }
  p {
    margin: 6 / @rem2 0 0;
    color: fade(#fff, 45%);
    font-size: 13 / @rem2;
  }
}
.metric-tabs {
  display: flex;
  gap: 8 / @rem2;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.metric-tabs__btn {
  border: 1 / @rem2 solid fade(@assistColor, 35%);
  background: rgba(8, 28, 64, 0.65);
  color: #9eb6dc;
  padding: 8 / @rem2 16 / @rem2;
  border-radius: 4 / @rem2;
  font-size: 14 / @rem2;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background 0.2s, box-shadow 0.2s;
  &:hover {
    color: #fff;
    border-color: fade(@mainColor, 55%);
  }
  &.active {
    color: #041428;
    font-weight: 600;
    border-color: #5ad6ff;
    background: linear-gradient(180deg, #7ee7ff 0%, #2aa8e8 100%);
    box-shadow: 0 0 14 / @rem2 rgba(90, 214, 255, 0.35);
  }
}
.branch-rank__body {
  flex: 1;
  min-height: 0;
  padding: 16 / @rem2 18 / @rem2;
  border: 1 / @rem2 solid fade(@assistColor, 25%);
  border-radius: 8 / @rem2;
  background: rgba(6, 22, 52, 0.55);
  overflow: auto;
}
.empty {
  margin: 40 / @rem2 auto;
  text-align: center;
  color: fade(#fff, 45%);
  font-size: 14 / @rem2;
}
.bars {
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
  padding-top: 42 / @rem2;
}
.bar-row {
  --fill: #1a6dff;
  --fill2: #00c3ff;
  display: grid;
  grid-template-columns: 36 / @rem2 minmax(0, 1fr) minmax(180 / @rem2, 32%);
  align-items: center;
  gap: 12 / @rem2;
  width: 100%;
  padding: 8 / @rem2 12 / @rem2;
  border: 1 / @rem2 solid fade(@assistColor, 18%);
  border-radius: 6 / @rem2;
  background: rgba(255, 255, 255, 0.03);
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, transform 0.2s;
  animation: barIn 0.45s ease both;
  animation-delay: calc(var(--i, 0) * 0.035s);
  &:hover {
    border-color: fade(@mainColor, 55%);
    background: rgba(0, 195, 255, 0.08);
    transform: translateX(4 / @rem2);
    z-index: 2;
    .bar-row__tip {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    .bar-row__fill {
      filter: brightness(1.12);
    }
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    border-color: @mainColor;
    box-shadow: 0 0 0 2 / @rem2 fade(@mainColor, 35%);
  }
}
.bar-row__rank {
  width: 28 / @rem2;
  height: 28 / @rem2;
  line-height: 28 / @rem2;
  text-align: center;
  border-radius: 50%;
  font-size: 13 / @rem2;
  font-weight: 700;
  color: fade(#fff, 70%);
  background: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  &.is-medal {
    color: #1a2438;
  }
}
.bar-row__track {
  position: relative;
  height: 28 / @rem2;
  border-radius: 4 / @rem2;
  background: rgba(8, 30, 70, 0.75);
  overflow: visible;
}
.bar-row__fill {
  position: relative;
  height: 100%;
  min-width: 8 / @rem2;
  border-radius: 4 / @rem2;
  background: linear-gradient(90deg, var(--fill), var(--fill2));
  box-shadow: 0 0 12 / @rem2 fade(#00c3ff, 25%);
  transition: width 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}
.bar-row__shine {
  position: absolute;
  inset: 0 auto 0 0;
  width: 40%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.28),
    transparent
  );
  transform: translateX(-120%);
  animation: shine 2.8s ease-in-out infinite;
}
.bar-row__tip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + (8 / @rem2));
  transform: translateX(-50%) translateY(6 / @rem2);
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 2 / @rem2;
  min-width: 132 / @rem2;
  padding: 8 / @rem2 12 / @rem2;
  border-radius: 4 / @rem2;
  border: 1 / @rem2 solid fade(@assistColor, 40%);
  background: rgba(4, 18, 42, 0.96);
  box-shadow: 0 8 / @rem2 24 / @rem2 rgba(0, 0, 0, 0.4);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s, transform 0.2s;
  white-space: nowrap;
  strong {
    color: #fff;
    font-size: 13 / @rem2;
  }
  em {
    font-style: normal;
    font-size: 12 / @rem2;
    &.up {
      color: #36d7b7;
    }
    &.down {
      color: #ff8a3d;
    }
    &.flat {
      color: fade(#fff, 55%);
    }
  }
}
.bar-row__label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8 / @rem2;
  min-width: 0;
  strong {
    color: #e8f4ff;
    font-size: 15 / @rem2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    flex-shrink: 0;
    color: @mainColor;
    font-size: 15 / @rem2;
    font-weight: 600;
  }
}
.medal-1 {
  --fill: #c9921a;
  --fill2: #ffe9a0;
  border-color: fade(#f5c542, 55%);
  background: linear-gradient(
    90deg,
    rgba(200, 150, 40, 0.28),
    rgba(255, 255, 255, 0.03)
  );
  .bar-row__rank {
    background: linear-gradient(145deg, #ffe9a0 0%, #f5c542 42%, #c9921a 100%);
    box-shadow: 0 0 10 / @rem2 rgba(245, 197, 66, 0.45);
  }
  .bar-row__label span {
    color: #f5c542;
  }
}
.medal-2 {
  --fill: #8e99ad;
  --fill2: #f2f5fa;
  border-color: fade(#c8d0e0, 50%);
  background: linear-gradient(
    90deg,
    rgba(160, 170, 190, 0.24),
    rgba(255, 255, 255, 0.03)
  );
  .bar-row__rank {
    background: linear-gradient(145deg, #f2f5fa 0%, #c0c8d8 45%, #8e99ad 100%);
    box-shadow: 0 0 10 / @rem2 rgba(192, 200, 216, 0.4);
  }
  .bar-row__label span {
    color: #d0d8e8;
  }
}
.medal-3 {
  --fill: #9a5a30;
  --fill2: #f0c49a;
  border-color: fade(#d08a55, 50%);
  background: linear-gradient(
    90deg,
    rgba(160, 100, 50, 0.26),
    rgba(255, 255, 255, 0.03)
  );
  .bar-row__rank {
    background: linear-gradient(145deg, #f0c49a 0%, #d08a55 45%, #9a5a30 100%);
    box-shadow: 0 0 10 / @rem2 rgba(208, 138, 85, 0.4);
  }
  .bar-row__label span {
    color: #e0a070;
  }
}
@keyframes barIn {
  from {
    opacity: 0;
    transform: translateY(8 / @rem2);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes shine {
  0%,
  40% {
    transform: translateX(-120%);
  }
  70%,
  100% {
    transform: translateX(280%);
  }
}
</style>
